import { SegmentType } from "src/Modules/Flights/constants";
import { Tooltip } from "antd";
import i18n from "src/i18n";
import {
  BaggageDynmaic,
  PassengerInputType,
  SSR,
  SSR_OPTIONS,
  SSROptionsKeys,
  SSROptionsType,
} from "src/Modules/Flights/constants/JourneyDetails";
import { MealDynamic } from "src/Modules/Flights/constants/JourneyDetails";
import { RowSeat, SeatType } from "src/Modules/Flights/constants/JourneyDetails";
import { SSRFlightOptionType } from "src/Modules/Flights/constants/JourneyDetails";

const isSeatsOptionsValid = (ssrObject?: SSR): boolean => {
  if (!ssrObject || !ssrObject.seatDynamic) return false;
  const isValid = ssrObject.seatDynamic.some((segmentSeatWrapper) =>
    segmentSeatWrapper.segmentSeat.some((segment) => segment.rowSeats.some((row) => row.seats.some((seat) => seat.code !== "NoSeat"))),
  );
  return isValid;
};

const isBaggageOptionsValid = (ssrObject?: SSR): boolean => {
  if (!ssrObject || !ssrObject.baggage) return false;
  const isValid = ssrObject.baggage.some((segmentSeatWrapper) => segmentSeatWrapper.some((baggage) => baggage.weight !== 0));
  return isValid;
};

const isMealOptionsValid = (ssrObject?: SSR): boolean => {
  if (!ssrObject || !ssrObject.mealDynamic) return false;
  const isValid = ssrObject.mealDynamic.some((mealDynamic) => mealDynamic.some((meal) => meal.quantity !== 0));
  return isValid;
};

const generateSSROptions = (ssrObject?: SSR) => {
  const allowedKeys = new Set(
    Object.entries(ssrObject ?? {})
      .filter(([key, value]) => {
        if (key === "seatDynamic") return isSeatsOptionsValid(ssrObject);
        if (key === "baggage") return isBaggageOptionsValid(ssrObject);
        if (key === "mealDynamic") return isMealOptionsValid(ssrObject);
        return Boolean(value);
      })
      .map(([key]) => key),
  );
  const options = SSR_OPTIONS.filter((option) => allowedKeys.has(option.backendKey));
  return {
    options,
    initialOption: options?.[0],
  };
};

const generateFlightOptions = (segments: SegmentType[]) => {
  const options = segments.flatMap((segment, segmentIndex) => {
    return segment.flights.flatMap((flight, flightIndex) => {
      return {
        segmentIndex,
        flightIndex,
        value: `${flightIndex}-${segmentIndex}`,
        origin: flight.origin,
        destination: flight.destination,
        duration: flight.durationMinutes,
      };
    });
  });
  return { options, initialOption: options?.[0] };
};

const generatePassengerOptions = (passengersInput: PassengerInputType) => {
  const options = passengersInput.passengers.map((passenger, index) => {
    const firstName = !passenger?.basicData?.firstName ? i18n.t("Flights.details.passengers.passenger") : passenger?.basicData?.firstName;
    const lastName = !passenger?.basicData?.lastName ? `${index + 1}` : passenger?.basicData?.lastName;
    return {
      firstName,
      lastName,
      value: `passenger-${index + 1}`,
      type: passenger.type,
    };
  });
  return { options, initialOption: options?.[0] };
};

export const updateSSR = (
  passengers: PassengerInputType,
  flight: SSRFlightOptionType,
  passengerIndex: number,
  ssr: SSROptionsType,
  value: Record<string, any>,
) => {
  const { ssrSelectionType, value: ssrKey, isMulti } = ssr!;
  const { segmentIndex, flightIndex } = flight!;

  const computeNewValue = (existing: Record<string, any> | undefined) => {
    if (!isMulti) return value;
    return { ...(existing ?? {}), ...value };
  };

  const updateFlightSSR = (f: any, fi: number) =>
    fi !== flightIndex
      ? f
      : {
          ...f,
          ssr: {
            ...f.ssr,
            [ssrKey]: computeNewValue(f.ssr?.[ssrKey]),
          },
        };

  const updateSegment = (seg: any, si: number) => {
    if (si !== segmentIndex) return seg;

    if (ssrSelectionType === "segment") {
      return {
        ...seg,
        ssr: {
          ...seg.ssr,
          [ssrKey]: computeNewValue(seg.ssr?.[ssrKey]),
        },
      };
    }
    return {
      ...seg,
      flights: seg.flights.map(updateFlightSSR),
    };
  };

  return passengers.passengers.map((p, i) =>
    i !== passengerIndex
      ? p
      : {
          ...p,
          segments: p.segments.map(updateSegment),
        },
  );
};

const generateMealOptions = (selectedFlight?: SSRFlightOptionType, mealDynamic?: MealDynamic) => {
  const options = mealDynamic?.[selectedFlight?.segmentIndex ?? 0]?.filter((meal) => {
    return selectedFlight?.origin.code == meal.origin;
  });
  return options;
};

export const generateBaggageOptions = (selectedFlight?: SSRFlightOptionType, baggageDynamic?: BaggageDynmaic) => {
  return baggageDynamic?.[selectedFlight?.segmentIndex ?? 0];
};

export const getMealLabel = (meal: { code: string; airlineDescription: string | null }) =>
  meal.code === "NoMeal" ? i18n.t("Flights.details.ssr.noMeal") : meal.airlineDescription;

export const getBaggageLabel = (baggage: { code: string; weight: number }) =>
  baggage.code === "NoBaggage" ? i18n.t("Flights.details.ssr.noBaggage") : i18n.t("Flights.details.ssr.weightKg", { weight: baggage.weight });

const formulateSSRTitle = (
  mealItem: Record<string, { price: number; description: string; quantity: number; weight: number }>,
  type: "meals" | "seats" | "baggages",
) => {
  const ssrFormulated = Object.entries(mealItem)
    .filter(([, mealValue]) => Object.keys(mealValue).length !== 0 && mealValue?.quantity !== 0)
    .map(([mealKey, mealValue]) => {
      return (
        <Tooltip
          title={type === "baggages" ? i18n.t("Flights.details.ssr.weightKg", { weight: mealValue.weight }) : mealValue?.description}
          key={mealKey}
          classNames={{
            container: "bg-background-primary! text-font-inverted! p-2!",
            arrow: "text-background-primary! border-border-active!",
          }}
        >
          {mealKey} {mealValue?.quantity ? `x ${mealValue.quantity}` : ""}
        </Tooltip>
      );
    });
  const content = ssrFormulated.length > 0 &&  ssrFormulated;

  return <span className="flex items-center gap-1">{content}</span>;
};
const formulateSSRforPassengerTile = (ssrItem: Record<string, any>, type?: SSROptionsKeys) => {
  if (type === "meals") return formulateSSRTitle(ssrItem, type);
  if (type === "seats") return formulateSSRTitle(ssrItem, type);
  if (type === "baggages") return formulateSSRTitle(ssrItem, type);
};

const getMaxLength = (row: RowSeat[]) => {
  if (!row) return null;
  const seatsCount = Math.max(...row.map((r) => r.seats?.length ?? 0));
  const seatsRow = row.find((r) => r.seats?.length === seatsCount)?.seats ?? [];
  return { seatsCount, seatsRow };
};

const segmentRow = (numberOfSeats: number, row: SeatType[]) => {
  const segments = [];
  let i = 0;
  while (i < numberOfSeats) {
    if (row?.[i].seatType === "Window" || row?.[i].seatType === "Aisle") {
      let j = i + 1;
      while (j < numberOfSeats && row?.[j].seatType !== "Window" && row?.[j].seatType !== "Aisle") {
        j++;
      }
      if (j < numberOfSeats) {
        segments.push(row.slice(i, j + 1).map((element) => element.seatNo));
        i = j + 1;
      } else {
        break;
      }
    } else {
      i++;
    }
  }
  return segments;
};
const generateRowSeatMap = (segments: (string | null)[][]) => {
  const gapsTotal = segments.length - 1;
  const colMap = new Map();
  let colStart = 1;
  for (let i = 0; i < segments.length; i++) {
    for (let j = 0; j < segments[i].length; j++) {
      colMap.set(colStart, segments[i][j]);
      colStart++;
    }
    if (i < segments.length - 1) {
      colMap.set(colStart++, "GAP");
    }
  }
  return {
    colMap,
    gapsTotal,
  };
};

const getSeatMapping = (row: RowSeat[]) => {
  const maxLength = getMaxLength(row);
  if (!maxLength) return null;
  const { seatsCount, seatsRow } = maxLength;
  const segments = segmentRow(seatsCount, seatsRow);
  const rowMap = generateRowSeatMap(segments);
  return {
    ...rowMap,
    seatsCount,
  };
};

export {
  generateSSROptions,
  generateFlightOptions,
  generatePassengerOptions,
  generateMealOptions,
  formulateSSRforPassengerTile,
  getSeatMapping,
};
